import { Component } from '@theme/component';
import { PaintFilterChangedEvent, PaintFilterClearedEvent } from '@theme/events';

/**
 * @typedef {Object} PaintApplicationFilterRefs
 * @property {HTMLElement} container - Filter container
 * @property {HTMLButtonElement[]} filterButtons - Filter button elements
 * @property {HTMLElement} legend - Filter legend element
 */

/**
 * Paint Application Filter Component
 * Manages filtering of product variants based on paint application
 * @extends {Component<PaintApplicationFilterRefs>}
 */
export default class PaintApplicationFilter extends Component {
  /** @type {string | null} */
  #currentFilter = null;

  /** @type {string[]} */
  #availableApplications = [];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #hasUserInteracted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeData();
    this.#setupAccessibility();
    
    // Delay initial state sync to ensure DOM is ready
    setTimeout(() => {
      this.#syncInitialState();
    }, 0);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  /**
   * Sync initial state with checked radio button
   * All buttons are always enabled in paint application filter
   * @private
   */
  #syncInitialState() {
    // Always use the Liquid template's first compatible application
    const firstCompatible = this.dataset.debugFirstCompatible;
    
    // Ensure all buttons are enabled (they should already be, but just in case)
    this.refs.filterButtons.forEach((input) => {
      input.disabled = false;
      input.checked = false;
    });
    
    if (firstCompatible && firstCompatible !== 'null' && firstCompatible !== '') {
      // Check the button for the first compatible application
      const buttonToCheck = this.querySelector(`input[data-application="${firstCompatible}"]`);
      
      if (buttonToCheck) {
        buttonToCheck.checked = true;
        
        // Set internal state
        this.#currentFilter = firstCompatible;
        
        // Dispatch initial filter event
        const event = new PaintFilterChangedEvent(firstCompatible);
        document.dispatchEvent(event);
      }
    } else {
      // Fallback: find any checked radio button
      const checkedButton = this.querySelector('input[type="radio"]:checked');
      
      if (checkedButton) {
        const application = checkedButton.dataset.application;
        
        // Set internal state
        this.#currentFilter = application;
        
        // Dispatch initial filter event
        const event = new PaintFilterChangedEvent(application);
        document.dispatchEvent(event);
      }
    }
  }

  /**
   * Initialize component data from attributes
   * @private
   */
  #initializeData() {
    // console.log('🔍 Initializing data...');
    // console.log('🔍 Dataset applications (raw):', this.dataset.applications);
    // console.log('🔍 Dataset applications type:', typeof this.dataset.applications);
    // console.log('🔍 Dataset applications length:', this.dataset.applications?.length);
    // console.log('🔍 Debug has applications:', this.dataset.debugHasApplications);
    // console.log('🔍 Debug first compatible:', this.dataset.debugFirstCompatible);
    // console.log('🔍 Debug current variant:', this.dataset.debugCurrentVariant);
    
    try {
      const applicationsData = this.dataset.applications;
      // console.log('🔍 Step 1 - Raw applicationsData:', applicationsData);
      
      if (applicationsData) {
        // console.log('🔍 Step 2 - About to parse JSON...');
        // console.log('🔍 Step 2 - JSON string to parse:', JSON.stringify(applicationsData));
        
        this.#availableApplications = JSON.parse(applicationsData);
        // console.log('🔍 Step 3 - JSON.parse result:', this.#availableApplications);
        // console.log('🔍 Step 3 - Result type:', typeof this.#availableApplications);
        // console.log('🔍 Step 3 - Is array:', Array.isArray(this.#availableApplications));
        // console.log('🔍 Step 3 - Array length:', this.#availableApplications?.length);
        
        if (Array.isArray(this.#availableApplications)) {
          this.#availableApplications.forEach((app, index) => {
            // console.log(`🔍 Step 4 - Application ${index}:`, app, 'type:', typeof app);
          });
        }
      } else {
        console.warn('❌ No applications data found');
      }
    } catch (error) {
      console.warn('❌ Failed to parse applications data:', error);
      console.warn('❌ Error details:', error.message);
      console.warn('❌ Error stack:', error.stack);
      this.#availableApplications = [];
    }
  }

  /**
   * Setup accessibility features
   * @private
   */
  #setupAccessibility() {
    // console.log('🔍 Setting up accessibility...');
    // console.log('🔍 Filter buttons found:', this.refs.filterButtons);
    
    if (!this.refs.filterButtons?.length) {
      console.warn('❌ No filter buttons found for accessibility setup');
      return;
    }
    
    this.refs.filterButtons.forEach((button, index) => {
      // console.log(`🔍 Setting up button ${index}:`, button);
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
    
    // console.log('🔍 Accessibility setup complete');
  }

  /**
   * Enable all options by removing disabled states
   * Called after first user interaction
   * Note: All options are always enabled in paint application filter, this is for variant-picker communication
   * @private
   */
  #enableAllOptions() {
    if (this.#hasUserInteracted) return; // Already interacted
    
    this.#hasUserInteracted = true;
    
    // Ensure all buttons are enabled (should already be, but just in case)
    this.refs.filterButtons.forEach(input => {
      input.disabled = false;
    });
    
    // Dispatch event to variant-picker to disable auto-selection
    const event = new CustomEvent('paint-filter:user-interacted', {
      bubbles: true,
      detail: { hasInteracted: true }
    });
    document.dispatchEvent(event);
    
    console.log('🔍 Paint filter user interaction detected, disabling auto-selection in variant steps');
  }

  /**
   * Handle filter button click
   * @param {MouseEvent} event
   */
  handleFilterClick(event) {
    const input = event.target.closest('input[type="radio"]');
    
    if (!input) {
      console.warn('❌ No radio input found in click event');
      return;
    }

    // All buttons are always enabled in paint application filter
    // Track first user interaction for variant-picker communication
    if (!this.#hasUserInteracted) {
      this.#enableAllOptions();
    }

    const application = input.dataset.application;
    
    // Always select the clicked application - prevent deselection
    // There must always be a selected application
    this.#selectApplication(application);
  }

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (!this.refs.filterButtons?.length) return;
    
    // All buttons are always enabled in paint application filter
    const inputs = this.refs.filterButtons;
    const currentIndex = inputs.indexOf(event.target);
    
    if (currentIndex === -1) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        
        // Track first user interaction for variant-picker communication
        if (!this.#hasUserInteracted) {
          this.#enableAllOptions();
        }
        
        const application = event.target.dataset.application;
        
        // Always select the pressed application - prevent deselection
        // There must always be a selected application
        this.#selectApplication(application);
        break;
        
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % inputs.length;
        inputs[nextIndex].focus();
        break;
        
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = currentIndex === 0 ? inputs.length - 1 : currentIndex - 1;
        inputs[prevIndex].focus();
        break;
        
      case 'Escape':
        // Don't allow deselection via Escape key
        // There must always be a selected application
        event.preventDefault();
        break;
    }
  }

  /**
   * Select an application filter
   * @param {string} application
   * @private
   */
  #selectApplication(application) {
    // console.log('🔍 #selectApplication called with:', application);
    // console.log('🔍 Available filter buttons:', this.refs.filterButtons);
    
    // Uncheck all inputs first
    this.refs.filterButtons.forEach(input => {
      // console.log('🔍 Unchecking input:', input, 'was checked:', input.checked);
      input.checked = false;
    });
    
    // Check the selected input
    const selectedInput = this.refs.filterButtons.find(input => 
      input.dataset.application === application
    );
    
    // console.log('🔍 Selected input found:', selectedInput);
    
    if (selectedInput) {
      selectedInput.checked = true;
      // console.log('🔍 Set selected input to checked:', selectedInput.checked);
    }
    
    this.#currentFilter = application;
    // console.log('🔍 Current filter set to:', this.#currentFilter);
    this.#dispatchFilterEvent(application);
  }

  /**
   * Dispatch filter changed event
   * @param {string} application
   * @private
   */
  #dispatchFilterEvent(application) {
    // console.log('🔍 #dispatchFilterEvent called with:', application);
    // console.log('🔍 Application type:', typeof application);
    // console.log('🔍 Application length:', application?.length);
    
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    
    // console.log('🔍 Creating PaintFilterChangedEvent...');
    const event = new PaintFilterChangedEvent(application);
    // console.log('🔍 Event created:', event);
    // console.log('🔍 Event detail:', event.detail);
    // console.log('🔍 Event detail type:', typeof event.detail);
    // console.log('🔍 Event detail application:', event.detail?.application);
    // console.log('🔍 Event detail application type:', typeof event.detail?.application);
    
    // Dispatch on document to ensure it reaches all components
    // console.log('🔍 Dispatching event on document...');
    document.dispatchEvent(event);
    // console.log('🔍 Event dispatched successfully');
  }

  /**
   * Clear the current filter
   * @private
   */
  #clearFilter() {
    // Uncheck all inputs
    this.refs.filterButtons.forEach(input => {
      input.checked = false;
    });
    
    this.#currentFilter = null;
    this.#dispatchClearEvent();
  }

  /**
   * Dispatch filter cleared event
   * @private
   */
  #dispatchClearEvent() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    
    const event = new PaintFilterClearedEvent();
    // Dispatch on document to ensure it reaches all components
    document.dispatchEvent(event);
  }

  /**
   * Get current filter state
   * @returns {string | null}
   */
  get currentFilter() {
    return this.#currentFilter;
  }

  /**
   * Get available applications
   * @returns {string[]}
   */
  get availableApplications() {
    return [...this.#availableApplications];
  }

  /**
   * Update button states based on current filter
   * Public method for external updates
   */
  updateButtonStates() {
    // console.log('🔍 Updating button states, current filter:', this.#currentFilter);
    
    this.refs.filterButtons.forEach(input => {
      const application = input.dataset.application;
      const shouldBeChecked = this.#currentFilter === application;
      
      // console.log(`🔍 Button ${application}: ${shouldBeChecked ? 'checked' : 'unchecked'}`);
      input.checked = shouldBeChecked;
    });
  }
}

// Register custom element
if (!customElements.get('paint-application-filter')) {
  if (!customElements.get('paint-application-filter')) customElements.define('paint-application-filter', PaintApplicationFilter);
}
